import { Preferences } from '@capacitor/preferences';

const values = new Map<string, string>();
let pendingWrites: Promise<void> = Promise.resolve();
let writeError: unknown;
let revision = 0;

export async function initializePreferences(): Promise<void> {
  const { keys } = await Preferences.keys();
  await Promise.all(
    keys.map(async (key) => {
      const { value } = await Preferences.get({ key });
      if (value !== null) values.set(key, value);
    })
  );

  // Move existing student data once; leave Firebase and third-party browser storage alone.
  window.removeEventListener('storage', refreshBrowserPreference);
  window.addEventListener('storage', refreshBrowserPreference);
  if (values.get('tupai-preferences-migrated') === '1') return;
  let previous: Storage;
  try {
    previous = window.localStorage;
  } catch {
    return;
  }
  const studentKeys = Object.keys(previous).filter((key) =>
    /^(tupai[_.-]|exam-attempt:)/.test(key)
  );
  for (const key of studentKeys) {
    const value = previous.getItem(key);
    if (value !== null && !values.has(key)) {
      await Preferences.set({ key, value });
      values.set(key, value);
    }
  }
  await Preferences.set({ key: 'tupai-preferences-migrated', value: '1' });
  for (const key of studentKeys) {
    try {
      previous.removeItem(key);
    } catch {
      console.warn('[storage] Unable to clear previous browser storage.');
    }
  }
}

async function refreshBrowserPreference(event: StorageEvent): Promise<void> {
  if (!event.key?.startsWith('CapacitorStorage.')) return;
  const key = event.key.slice('CapacitorStorage.'.length);
  const beforeRead = revision;
  try {
    const { value } = await Preferences.get({ key });
    if (revision !== beforeRead) return;
    if (value === null) values.delete(key);
    else values.set(key, value);
    if (key === 'tupai_token') window.dispatchEvent(new Event('student-auth-changed'));
  } catch {
    console.warn('[storage] Unable to refresh preferences from another tab.');
  }
}

function queueWrite(operation: () => Promise<void>): void {
  pendingWrites = pendingWrites.then(operation).catch((error) => {
    writeError = error;
    console.warn('[storage] Unable to save preferences.');
  });
}

// Startup hydrates this cache before importing routes or components that read synchronously.
export const preferencesStorage = {
  getItem(key: string): string | null {
    return values.get(key) ?? null;
  },
  setItem(key: string, value: string): void {
    revision++;
    values.set(key, value);
    queueWrite(() => Preferences.set({ key, value }));
  },
  removeItem(key: string): void {
    revision++;
    values.delete(key);
    queueWrite(() => Preferences.remove({ key }));
  },
};

export async function flushPreferences(): Promise<void> {
  await pendingWrites;
  if (writeError) {
    const error = writeError;
    writeError = undefined;
    throw error;
  }
}

if (import.meta.hot) {
  import.meta.hot.dispose(() => window.removeEventListener('storage', refreshBrowserPreference));
}
